/**
 * The branch read: against target by week × discipline group × territory, in
 * points. The arithmetic keeps three layers — `plotted`, `provisional`,
 * `estimated` — because `committedPoints` is the number the referral panel and
 * Act 5 both quote and it must not move. **The screens draw two**: Planned is
 * `plotted`, and Reserved is `provisional + estimated`, regrouped by reason in
 * `reserved` (held for SOC, pending auth, awaiting plan — see `engine/reserved`).
 *
 * Supply (the target) is the sum of the weekly targets of the clinicians who own
 * the territory for that discipline, less PTO; demand lands by the patient's
 * territory. The same selector answers the referral panel's capacity row and
 * fills every heat-map cell.
 *
 * The minutes grid underneath (capacity / booked / reserved) is kept for the SOC
 * lane and the hours engine.
 */
import type { DateKey, DemoState, Discipline, DisciplineGroup, Hold, RosterEntry } from "@caredna-marlin/utils/homeHealthDemo/state";
import { STANDING_PATTERNS } from "@caredna-marlin/utils/homeHealthDemo/fixtures/standingPatterns";
import { addDaysToKey, dateKeyRange, isoWeekday, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { clinicianDayFacts, clinicianDaysInRange, type ClinicianDayFacts } from "./clinicianDay";
import { disciplineGroupFor, ownsTerritory, rosterEntries } from "./roster";
import { addReserved, emptyReserved, roundReserved, type ReservedByReason } from "./reserved";
import { placeholderPointsFor, planVisitLayers, roundPoints, selectWeekPoints, type WeekPoints } from "./utilization";
import { pointsFor } from "@caredna-marlin/utils/homeHealthDemo/engine/visitTypes";

export type Granularity = "day" | "week";

export interface CapacityGridOptions {
  rangeStart: DateKey;
  rangeEnd: DateKey;
  granularity: Granularity;
  includeReservations: boolean;
  disciplines?: Discipline[];
  territories?: string[];
}

export interface CapacityCell {
  /** DateKey for day granularity, Monday DateKey for week granularity. */
  bucket: DateKey;
  discipline: Discipline;
  territory: string;
  capacityMinutes: number;
  overflowMinutes: number;
  blockMinutes: number;
  bookedMinutes: number;
  reservedMinutes: number;
  openMinutes: number;
  clinicianCount: number;
}

export interface ReservedDemand {
  dateKey: DateKey;
  discipline: Discipline;
  territory: string;
  minutes: number;
  source: "hold" | "tail" | "sandbox";
}

export function bucketFor(dateKey: DateKey, granularity: Granularity): DateKey {
  return granularity === "week" ? weekStartKey(dateKey) : dateKey;
}

export function holdMinutes(state: Pick<DemoState, "config">): number {
  return state.config.socSlot.durationMinutes + state.config.socSlot.driveMinutes;
}

/**
 * **The weekdays a hold's tail covers**: from the day after the start of care,
 * for the plan's weeks. Its weeks are anchored on the SOC, so the rest of the
 * SOC's own week carries a share too. Starting on the Monday after left a Friday
 * seed's next week — the SOC's own week — with nothing a template could change,
 * so 1.2's row could not move there (2026-10-12).
 */
export function tailWeekdays(hold: Pick<Hold, "date">, tail: Pick<Hold["tail"][number], "weeks">, from: DateKey, to: DateKey): DateKey[] {
  const first = addDaysToKey(hold.date, 1);
  const last = addDaysToKey(hold.date, 7 * tail.weeks);
  const start = first > from ? first : from;
  const end = last < to ? last : to;
  return start > end ? [] : dateKeyRange(start, end).filter((d) => isoWeekday(d) <= 5);
}

/** Reserved demand from placeholders: held SOC holds on their day; tails spread over the weekdays after it. */
export function reservedDemand(state: DemoState, rangeStart: DateKey, rangeEnd: DateKey): ReservedDemand[] {
  const out: ReservedDemand[] = [];
  for (const hold of Object.values(state.holds)) {
    if (hold.status !== "held") continue;
    const patient = state.patients[hold.patientRef];
    const territory = patient?.territory ?? "Unassigned";
    if (hold.date >= rangeStart && hold.date <= rangeEnd) {
      out.push({ dateKey: hold.date, discipline: socDiscipline(state, hold), territory, minutes: holdMinutes(state), source: "hold" });
    }
    for (const tail of hold.tail) {
      const perWeekday = (tail.hoursPerWeek * 60) / 5;
      for (const dateKey of tailWeekdays(hold, tail, rangeStart, rangeEnd)) {
        out.push({ dateKey, discipline: tail.discipline, territory, minutes: Math.round(perWeekday), source: "tail" });
      }
    }
  }
  for (const pv of Object.values(state.planVisits)) {
    if (pv.status !== "placeholder" || pv.targetDate < rangeStart || pv.targetDate > rangeEnd) continue;
    const episode = state.episodes[pv.episodeId];
    const territory = episode ? state.patients[episode.patientRef]?.territory ?? "Unassigned" : "Unassigned";
    const minutes = state.config.visitTypes[pv.visitTypeKind].plannedMinutes + state.config.socSlot.driveMinutes;
    out.push({ dateKey: pv.targetDate, discipline: pv.discipline, territory, minutes, source: "tail" });
  }
  for (const c of state.sandbox?.addedCases ?? []) {
    const perWeekday = (c.hoursPerWeek * 60) / 5;
    for (let w = 0; w < c.weeks; w++) {
      const weekStart = addDaysToKey(weekStartKey(c.startWeek), 7 * w);
      for (let d = 0; d < 5; d++) {
        const dateKey = addDaysToKey(weekStart, d);
        if (dateKey < rangeStart || dateKey > rangeEnd) continue;
        out.push({ dateKey, discipline: c.discipline, territory: c.territory, minutes: Math.round(perWeekday), source: "sandbox" });
      }
    }
  }
  return out;
}

function socDiscipline(state: DemoState, hold: Hold): Discipline {
  const handle = hold.providerHandle;
  return handle && state.roster[handle] ? state.roster[handle].discipline : "RN";
}

export function selectCapacityGrid(state: DemoState, opts: CapacityGridOptions): CapacityCell[] {
  const days = dateKeyRange(opts.rangeStart, opts.rangeEnd);
  const facts = clinicianDaysInRange(state, days);
  const disciplineOf = Object.fromEntries(rosterEntries(state).map((e) => [e.handle, e.discipline])) as Record<string, Discipline>;
  const cells = new Map<string, CapacityCell>();
  const key = (bucket: DateKey, discipline: Discipline, territory: string) => `${bucket}|${discipline}|${territory}`;
  const cell = (bucket: DateKey, discipline: Discipline, territory: string): CapacityCell => {
    const k = key(bucket, discipline, territory);
    let c = cells.get(k);
    if (!c) {
      c = { bucket, discipline, territory, capacityMinutes: 0, overflowMinutes: 0, blockMinutes: 0, bookedMinutes: 0, reservedMinutes: 0, openMinutes: 0, clinicianCount: 0 };
      cells.set(k, c);
    }
    return c;
  };

  const counted = new Set<string>();
  for (const f of facts) {
    const discipline = disciplineOf[f.handle];
    if (!discipline) continue;
    if (opts.disciplines && !opts.disciplines.includes(discipline)) continue;
    const bucket = bucketFor(f.dateKey, opts.granularity);
    if (f.hasShift) {
      for (const [territory, share] of Object.entries(f.supplyShares)) {
        const supply = cell(bucket, discipline, territory);
        supply.capacityMinutes += Math.round((f.shiftMinutes - f.blockMinutes) * share);
        supply.overflowMinutes += Math.round(f.overflowMinutes * share);
        supply.blockMinutes += Math.round(f.blockMinutes * share);
        const ck = `${bucket}|${discipline}|${territory}|${f.handle}`;
        if (!counted.has(ck)) {
          counted.add(ck);
          supply.clinicianCount += 1;
        }
      }
    }
    for (const [territory, minutes] of Object.entries(f.demandByTerritory)) {
      cell(bucket, discipline, territory).bookedMinutes += minutes;
    }
  }

  if (opts.includeReservations) {
    for (const r of reservedDemand(state, opts.rangeStart, opts.rangeEnd)) {
      if (opts.disciplines && !opts.disciplines.includes(r.discipline)) continue;
      cell(bucketFor(r.dateKey, opts.granularity), r.discipline, r.territory).reservedMinutes += r.minutes;
    }
  }

  const out = [...cells.values()].filter((c) => !opts.territories || opts.territories.includes(c.territory));
  for (const c of out) c.openMinutes = c.capacityMinutes - c.bookedMinutes - c.reservedMinutes;
  return out.sort((a, b) => a.bucket.localeCompare(b.bucket) || a.discipline.localeCompare(b.discipline) || a.territory.localeCompare(b.territory));
}

/** Roll cells up across territories (or disciplines) for a total row. */
export function rollUp(cells: CapacityCell[], by: "discipline" | "territory" | "bucket"): CapacityCell[] {
  const map = new Map<string, CapacityCell>();
  for (const c of cells) {
    const k = by === "discipline" ? `${c.bucket}|${c.discipline}` : by === "territory" ? `${c.bucket}|${c.territory}` : c.bucket;
    const acc = map.get(k) ?? { ...c, territory: by === "territory" ? c.territory : "All", discipline: by === "discipline" ? c.discipline : ("All" as Discipline), capacityMinutes: 0, overflowMinutes: 0, blockMinutes: 0, bookedMinutes: 0, reservedMinutes: 0, openMinutes: 0, clinicianCount: 0 };
    acc.capacityMinutes += c.capacityMinutes;
    acc.overflowMinutes += c.overflowMinutes;
    acc.blockMinutes += c.blockMinutes;
    acc.bookedMinutes += c.bookedMinutes;
    acc.reservedMinutes += c.reservedMinutes;
    acc.clinicianCount += c.clinicianCount;
    acc.openMinutes = acc.capacityMinutes - acc.bookedMinutes - acc.reservedMinutes;
    map.set(k, acc);
  }
  return [...map.values()];
}

export function weekdaysOnly(days: DateKey[]): DateKey[] {
  return days.filter((d) => isoWeekday(d) <= 5);
}

// ---------------------------------------------------------------------------
// Points by territory × discipline group × week — the three layers
// ---------------------------------------------------------------------------

export interface TerritoryDisciplineWeek {
  territory: string;
  group: DisciplineGroup;
  weekStart: DateKey;
  /** Real and inside-auth placeholder visits for patients in the territory. */
  plotted: number;
  /** Beyond-auth placeholders and SOC holds for patients in the territory. */
  provisional: number;
  /** Tails of accepted referrals and pending disciplines of admitted patients: load with no plan yet. */
  estimated: number;
  /**
   * The one **Reserved** layer the screens draw, split by the reason it carries.
   * It is exactly `provisional + estimated` regrouped: the three layers above are
   * the arithmetic, this is the word. `committedPoints` never reads it.
   */
  reserved: ReservedByReason;
  /** Σ weekly targets of the clinicians who own the territory for the group, less PTO days. */
  target: number;
  /** The owners, alphabetical, each with their own week against target. */
  clinicians: (WeekPoints & { displayName: string })[];
}

export function committedPoints(week: Pick<TerritoryDisciplineWeek, "plotted" | "provisional" | "estimated">): number {
  return roundPoints(week.plotted + week.provisional + week.estimated);
}

/** Weekday pattern days in the week with no shift when the horizon has shifts for that clinician: approved time off. */
export function ptoWeekdaysFor(state: DemoState, entry: RosterEntry, weekStart: DateKey): number {
  const pattern = STANDING_PATTERNS[entry.handle];
  if (!pattern || pattern.perDiem) return 0;
  const providerId = state.meta.bindings.providerIdsByHandle[entry.handle] ?? entry.providerId;
  if (providerId === undefined || !state.server.shifts.some((s) => s.providerId === providerId)) return 0;
  let pto = 0;
  for (const day of dateKeyRange(weekStart, addDaysToKey(weekStart, 6))) {
    const wd = isoWeekday(day);
    if (wd > 5 || !pattern.weekdays.includes(wd)) continue;
    if (!clinicianDayFacts(state, entry, day).hasShift) pto += 1;
  }
  return pto;
}

export function proratedTarget(targetPoints: number, ptoWeekdays: number): number {
  return roundPoints((targetPoints * Math.max(0, 5 - ptoWeekdays)) / 5);
}

export function selectTerritoryDisciplineWeek(state: DemoState, territory: string, group: DisciplineGroup, weekOf: DateKey): TerritoryDisciplineWeek {
  const weekStart = weekStartKey(weekOf);
  const weekEnd = addDaysToKey(weekStart, 6);
  const days = dateKeyRange(weekStart, weekEnd);
  const entries = rosterEntries(state).filter((e) => disciplineGroupFor(e.discipline) === group);
  const inTerritory = (patientRef: string | undefined) => !!patientRef && state.patients[patientRef]?.territory === territory;

  // Plotted: real visits (and the virtual census) by patient territory …
  let plotted = 0;
  for (const entry of entries) {
    for (const day of days) plotted += clinicianDayFacts(state, entry, day).pointsByTerritory[territory] ?? 0;
  }
  // … plus placeholders for the territory's patients, inside auth; beyond auth is provisional.
  const placeholders = placeholderPointsFor(state, weekStart, (v) => disciplineGroupFor(v.discipline) === group && inTerritory(state.episodes[v.episodeId]?.patientRef));
  plotted += placeholders.firm;
  const reserved = emptyReserved();
  let provisional = placeholders.provisional;
  reserved.pendingAuth += placeholders.provisional;
  for (const hold of Object.values(state.holds)) {
    if (hold.status !== "held" || hold.date < weekStart || hold.date > weekEnd || !inTerritory(hold.patientRef)) continue;
    if (disciplineGroupFor(socDiscipline(state, hold)) !== group) continue;
    provisional += pointsFor(state.config, "SOC");
    reserved.heldForSoc += pointsFor(state.config, "SOC");
  }

  // Estimated: tails of held referrals over the weekdays after the hold, a week's share per
  // weekday, and pending disciplines of admitted patients.
  let estimated = 0;
  for (const hold of Object.values(state.holds)) {
    if (hold.status !== "held" || !inTerritory(hold.patientRef)) continue;
    for (const tail of hold.tail) {
      if (disciplineGroupFor(tail.discipline) !== group) continue;
      const share = (tail.pointsPerWeek * tailWeekdays(hold, tail, weekStart, weekEnd).length) / 5;
      estimated += share;
      reserved.awaitingPlan += share;
    }
  }
  for (const episode of Object.values(state.episodes)) {
    if (!inTerritory(episode.patientRef)) continue;
    for (const pending of episode.pendingDisciplines) {
      if (disciplineGroupFor(pending.discipline) !== group) continue;
      const first = weekStartKey(state.meta.demoToday);
      const last = addDaysToKey(first, 7 * (pending.weeks - 1));
      if (weekStart >= first && weekStart <= last) {
        estimated += pending.pointsPerWeek;
        reserved.awaitingPlan += pending.pointsPerWeek;
      }
    }
  }

  const owners = entries.filter((e) => ownsTerritory(e, territory)).sort((a, b) => a.displayName.localeCompare(b.displayName));
  const clinicians = selectCellClinicians(state, group, [territory], weekStart);
  const target = owners.reduce((sum, e) => sum + proratedTarget(e.targetPoints, ptoWeekdaysFor(state, e, weekStart)), 0);

  return { territory, group, weekStart, plotted: roundPoints(plotted), provisional: roundPoints(provisional), estimated: roundPoints(estimated), reserved: roundReserved(reserved), target: roundPoints(target), clinicians };
}

/**
 * The clinicians behind a heat-map cell (5.1's hover): everyone who owns one of
 * the cell's territories for that discipline group, each once however many of
 * them they own, alphabetical, each with their own week from `selectWeekPoints`.
 * The hover, My week's header and the utilization strip therefore read the same
 * three numbers; the list is never ordered by points, so the screen cannot rank
 * clinicians against each other.
 */
export function selectCellClinicians(state: DemoState, group: DisciplineGroup, territories: string[], weekOf: DateKey): (WeekPoints & { displayName: string })[] {
  const weekStart = weekStartKey(weekOf);
  return rosterEntries(state)
    .filter((e) => disciplineGroupFor(e.discipline) === group && territories.some((t) => ownsTerritory(e, t)))
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((e) => ({ ...selectWeekPoints(state, e.handle, weekStart), displayName: e.displayName }));
}

/** Every territory × group cell for a run of weeks — the heat map. */
export function selectPointsGrid(state: DemoState, weekStarts: DateKey[], groups: DisciplineGroup[], territories: string[]): TerritoryDisciplineWeek[] {
  const out: TerritoryDisciplineWeek[] = [];
  for (const weekStart of weekStarts) for (const group of groups) for (const territory of territories) out.push(selectTerritoryDisciplineWeek(state, territory, group, weekStart));
  return out;
}

// ---------------------------------------------------------------------------
// The heat map's cells: the same three layers bucketed by day or by week
// ---------------------------------------------------------------------------

/**
 * A heat-map cell. Week buckets sum their days exactly — the target is
 * distributed over the days the owners actually work, so switching By day /
 * By week never changes the total.
 */
export interface PointsCell {
  bucket: DateKey;
  group: DisciplineGroup;
  territory: string;
  plotted: number;
  provisional: number;
  estimated: number;
  /** `provisional + estimated`, split by reason — the hatched layer and its hover. */
  reserved: ReservedByReason;
  target: number;
}

export interface PointsGridOptions {
  rangeStart: DateKey;
  rangeEnd: DateKey;
  granularity: Granularity;
  groups: DisciplineGroup[];
  territories: string[];
  /** Also emit one cell per discipline group over all the territories asked for (`GROUP_TOTAL_TERRITORY`). */
  groupTotals?: boolean;
}

/**
 * The `territory` of a discipline-group total cell. Demand sums across the
 * territories, because a visit belongs to exactly one; supply counts each
 * clinician **once**, because a clinician who owns East and West is one person.
 * Summing the territory rows instead would count Marcus twice, so the group row
 * would read against a target no roster could staff.
 */
export const GROUP_TOTAL_TERRITORY = "All";

/** The weekly target spread over the weekdays the clinician is actually working. */
export function targetByDay(state: DemoState, entry: RosterEntry, weekStart: DateKey): Record<DateKey, number> {
  const out: Record<DateKey, number> = {};
  const pattern = STANDING_PATTERNS[entry.handle];
  if (!pattern || pattern.perDiem) return out;
  const worked = dateKeyRange(weekStart, addDaysToKey(weekStart, 6)).filter((day) => isoWeekday(day) <= 5 && pattern.weekdays.includes(isoWeekday(day)) && clinicianDayFacts(state, entry, day).hasShift);
  const total = proratedTarget(entry.targetPoints, ptoWeekdaysFor(state, entry, weekStart));
  if (!worked.length || total <= 0) return out;
  for (const day of worked) out[day] = total / worked.length;
  return out;
}

/** Tails and pending disciplines are weekly loads; on a day bucket they spread over the five weekdays. */
function addEstimate(into: Map<string, number>, key: (bucket: DateKey) => string, days: DateKey[], granularity: Granularity, weekStart: DateKey, pointsPerWeek: number): void {
  if (granularity === "week") {
    into.set(key(weekStart), (into.get(key(weekStart)) ?? 0) + pointsPerWeek);
    return;
  }
  const weekdays = days.filter((d) => weekStartKey(d) === weekStart && isoWeekday(d) <= 5);
  for (const d of weekdays) into.set(key(d), (into.get(key(d)) ?? 0) + pointsPerWeek / 5);
}

export function selectPointsCells(state: DemoState, opts: PointsGridOptions): PointsCell[] {
  const days = dateKeyRange(opts.rangeStart, opts.rangeEnd);
  const buckets = opts.granularity === "week" ? Array.from(new Set(days.map(weekStartKey))) : days;
  const cells = new Map<string, PointsCell>();
  const key = (bucket: DateKey, group: DisciplineGroup, territory: string) => `${bucket}|${group}|${territory}`;
  for (const bucket of buckets) for (const group of opts.groups) for (const territory of opts.territories) cells.set(key(bucket, group, territory), { bucket, group, territory, plotted: 0, provisional: 0, estimated: 0, reserved: emptyReserved(), target: 0 });
  const cell = (bucket: DateKey, group: DisciplineGroup, territory: string): PointsCell | undefined => cells.get(key(bucketFor(bucket, opts.granularity), group, territory));
  const inScope = (group: DisciplineGroup, territory: string) => opts.groups.includes(group) && opts.territories.includes(territory);

  // Plotted: real visits (and the virtual census beyond the horizon) by patient territory.
  const byHandle = new Map(rosterEntries(state).map((e) => [e.handle, e]));
  for (const f of clinicianDaysInRange(state, days)) {
    const entry = byHandle.get(f.handle);
    if (!entry) continue;
    const group = disciplineGroupFor(entry.discipline);
    for (const [territory, points] of Object.entries(f.pointsByTerritory)) {
      if (!points || !inScope(group, territory)) continue;
      const c = cell(f.dateKey, group, territory);
      if (c) c.plotted += points;
    }
  }

  // Plan visits not yet placed: firm adds to plotted, beyond-auth adds to provisional.
  const layers = planVisitLayers(state);
  for (const v of Object.values(state.planVisits)) {
    const layer = layers[v.id];
    if (!layer || v.targetDate < opts.rangeStart || v.targetDate > opts.rangeEnd) continue;
    const episode = state.episodes[v.episodeId];
    const territory = episode ? state.patients[episode.patientRef]?.territory : undefined;
    const group = disciplineGroupFor(v.discipline);
    if (!territory || !inScope(group, territory)) continue;
    const c = cell(v.targetDate, group, territory);
    if (!c) continue;
    if (layer === "provisional") {
      c.provisional += pointsFor(state.config, v.visitTypeKind);
      c.reserved.pendingAuth += pointsFor(state.config, v.visitTypeKind);
    } else c.plotted += pointsFor(state.config, v.visitTypeKind);
  }

  // Holds are provisional on their day; their tails are the estimate in the weeks after.
  const estimates = new Map<string, number>();
  for (const hold of Object.values(state.holds)) {
    if (hold.status !== "held") continue;
    const territory = state.patients[hold.patientRef]?.territory;
    if (!territory) continue;
    const socGroup = disciplineGroupFor(socDiscipline(state, hold));
    if (hold.date >= opts.rangeStart && hold.date <= opts.rangeEnd && inScope(socGroup, territory)) {
      const c = cell(hold.date, socGroup, territory);
      if (c) {
        c.provisional += pointsFor(state.config, "SOC");
        c.reserved.heldForSoc += pointsFor(state.config, "SOC");
      }
    }
    for (const tail of hold.tail) {
      const group = disciplineGroupFor(tail.discipline);
      if (!inScope(group, territory)) continue;
      // The same weekdays the week selector counts (`tailWeekdays`), a fifth of a week each.
      for (const d of tailWeekdays(hold, tail, opts.rangeStart, opts.rangeEnd)) {
        const bucket = opts.granularity === "week" ? weekStartKey(d) : d;
        estimates.set(key(bucket, group, territory), (estimates.get(key(bucket, group, territory)) ?? 0) + tail.pointsPerWeek / 5);
      }
    }
  }
  // Disciplines that were ordered but whose frequencies have not landed.
  for (const episode of Object.values(state.episodes)) {
    const territory = state.patients[episode.patientRef]?.territory;
    if (!territory) continue;
    for (const pending of episode.pendingDisciplines) {
      const group = disciplineGroupFor(pending.discipline);
      if (!inScope(group, territory)) continue;
      for (let w = 0; w < pending.weeks; w++) {
        const weekStart = addDaysToKey(weekStartKey(state.meta.demoToday), 7 * w);
        addEstimate(estimates, (b) => key(b, group, territory), days, opts.granularity, weekStart, pending.pointsPerWeek);
      }
    }
  }
  for (const [k, points] of estimates) {
    const c = cells.get(k);
    if (c) {
      c.estimated += points;
      c.reserved.awaitingPlan += points;
    }
  }

  // Target: the owners' weekly targets, spread over the days they work.
  const weekStarts = Array.from(new Set(days.map(weekStartKey)));
  for (const entry of rosterEntries(state)) {
    const group = disciplineGroupFor(entry.discipline);
    if (!opts.groups.includes(group)) continue;
    const owned = entry.territories.filter((t) => opts.territories.includes(t));
    if (!owned.length) continue;
    for (const weekStart of weekStarts) {
      const perDay = targetByDay(state, entry, weekStart);
      for (const [day, points] of Object.entries(perDay)) {
        if (day < opts.rangeStart || day > opts.rangeEnd) continue;
        for (const territory of owned) {
          const c = cell(day, group, territory);
          if (c) c.target += points;
        }
      }
    }
  }

  // The discipline-group row: demand sums the territories, supply counts each
  // clinician once however many of those territories they own.
  if (opts.groupTotals) {
    for (const bucket of buckets) {
      for (const group of opts.groups) {
        const total: PointsCell = { bucket, group, territory: GROUP_TOTAL_TERRITORY, plotted: 0, provisional: 0, estimated: 0, reserved: emptyReserved(), target: 0 };
        for (const territory of opts.territories) {
          const c = cells.get(key(bucket, group, territory));
          if (!c) continue;
          total.plotted += c.plotted;
          total.provisional += c.provisional;
          total.estimated += c.estimated;
          addReserved(total.reserved, c.reserved);
        }
        cells.set(key(bucket, group, GROUP_TOTAL_TERRITORY), total);
      }
    }
    for (const entry of rosterEntries(state)) {
      const group = disciplineGroupFor(entry.discipline);
      if (!opts.groups.includes(group) || !entry.territories.some((t) => opts.territories.includes(t))) continue;
      for (const weekStart of Array.from(new Set(days.map(weekStartKey)))) {
        for (const [day, points] of Object.entries(targetByDay(state, entry, weekStart))) {
          if (day < opts.rangeStart || day > opts.rangeEnd) continue;
          const c = cells.get(key(bucketFor(day, opts.granularity), group, GROUP_TOTAL_TERRITORY));
          if (c) c.target += points;
        }
      }
    }
  }

  return [...cells.values()].map((c) => ({ ...c, plotted: roundPoints(c.plotted), provisional: roundPoints(c.provisional), estimated: roundPoints(c.estimated), reserved: roundReserved(c.reserved), target: roundPoints(c.target) }));
}

export type { ClinicianDayFacts };
